var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import CalendarPanel from "./CalendarPanel.js";
import YearPicker from "./YearPicker.js";
import DateHelper from "../helper/DateHelper.js";
import EventHelper from "../helper/EventHelper.js";
import ObjectHelper from "../helper/ObjectHelper.js";
import BrowserHelper from "../helper/BrowserHelper.js";
import Combo from "./Combo.js";
import DomHelper from "../helper/DomHelper.js";
import "./DisplayField.js";
const generateMonthNames = () => DateHelper.getMonthNames().map((m, i) => [i, m]), dateSort = (lhs, rhs) => lhs.valueOf() - rhs.valueOf(), { DateSet } = DateHelper, emptyArray = Object.freeze([]), consecutiveDates = (selection) => selection.every((date, i) => !i || !(DateHelper.add(selection[i - 1], 1, "d") - date));
class ReadOnlyCombo extends Combo {
  static get configurable() {
    return {
      editable: false,
      inputAttributes: {
        tag: "div",
        tabIndex: -1
      },
      inputValueAttr: "innerHTML",
      highlightExternalChange: false,
      monitorResize: false,
      triggers: {
        expand: false
      },
      picker: {
        align: {
          align: "t-b",
          axisLock: true,
          matchSize: false
        },
        cls: "b-readonly-combo-list",
        scrollable: {
          overflowX: false
        }
      }
    };
  }
}
__publicField(ReadOnlyCombo, "$name", "ReadOnlyCombo");
__publicField(ReadOnlyCombo, "type", "readonlycombo");
ReadOnlyCombo.initClass();
class DatePicker extends CalendarPanel {
  static get delayable() {
    return {
      refresh: "raf"
    };
  }
  static get configurable() {
    return {
      /**
       * The date that the user has navigated to using the UI *prior* to setting the widget's
       * value by selecting. The initial default is today's date. Can also be supplied as a `YYYY-MM-DD` date
       * string.
       *
       * This may be changed using keyboard navigation. The {@link Core.widget.CalendarPanel#property-date} is set
       * by pressing `ENTER` when the desired date is reached.
       *
       * Programmatically setting the {@link Core.widget.CalendarPanel#config-date}, or using the UI to select the
       * date by clicking it also sets the `activeDate`
       * @config {Date|'today'|String}
       */
      activeDate: {
        $config: "date",
        value: "today"
      },
      focusable: true,
      textContent: false,
      tbar: {
        overflow: null,
        items: {
          prevYear: {
            cls: "b-icon b-icon-first b-datepicker-nav-button",
            onAction: "up.gotoPrevYear",
            tooltip: "L{DatePicker.gotoPrevYear}",
            weight: 100
          },
          prevMonth: {
            cls: "b-icon b-icon-previous b-datepicker-nav-button",
            onAction: "up.gotoPrevMonth",
            tooltip: "L{DatePicker.gotoPrevMonth}",
            weight: 200
          },
          fields: {
            type: "container",
            cls: "b-datepicker-title",
            items: {
              monthField: {
                type: "readonlycombo",
                cls: "b-datepicker-monthfield",
                items: generateMonthNames(),
                internalListeners: {
                  select: "up.onMonthPicked"
                }
              },
              yearButton: {
                type: "button",
                cls: "b-datepicker-yearbutton",
                internalListeners: {
                  click: "up.onYearPickerRequested"
                }
              }
            },
            weight: 300
          },
          nextMonth: {
            cls: "b-icon b-icon-next b-datepicker-nav-button",
            onAction: "up.gotoNextMonth",
            tooltip: "L{DatePicker.gotoNextMonth}",
            weight: 400
          },
          nextYear: {
            cls: "b-icon b-icon-last b-datepicker-nav-button",
            onAction: "up.gotoNextYear",
            tooltip: "L{DatePicker.gotoNextYear}",
            weight: 500
          }
        }
      },
      yearPicker: {
        value: {
          type: "YearPicker",
          yearButtonCount: 16,
          trapFocus: true,
          positioned: true,
          hidden: true,
          internalListeners: {
            titleClick: "up.onYearPickerTitleClick",
            select: "up.onYearPicked"
          }
        },
        $config: "lazy"
      },
      /**
       * The initially selected date (or a `YYYY-MM-DD` date string).
       * @config {Date|String}
       */
      date: null,
      /**
       * The minimum selectable date. Selection of and navigation to dates prior
       * to this date will not be possible.
       * @config {Date}
       */
      minDate: {
        $config: "date",
        value: null
      },
      /**
       * The maximum selectable date. Selection of and navigation to dates after
       * this date will not be possible.
       * @config {Date}
       */
      maxDate: {
        $config: "date",
        value: null
      },
      /**
       * By default, disabled dates cannot be navigated to, and they are skipped over
       * during keyboard navigation. Configure this as `true` to enable navigation to
       * disabled dates.
       * @config {Boolean}
       * @default
       */
      focusDisabledDates: null,
      /**
       * Configure as `true` to enable selecting multiple discontiguous date ranges using
       * click and Shift+click to create ranges and Ctrl+click to select/deselect individual dates.
       *
       * Configure as `'simple'` to enable selecting a single date range by clicking a
       * start date followed by the end date. If you configure multiSelect `range` on touch-devices, `simple`
       * will be the mode used.
       *
       * Configure as `'range'` to enable selecting a single date range by selecting a
       * start and end date. Hold "SHIFT" button to select date range. Ctrl+click may add
       * or remove dates to/from either end of the range.
       * @config {Boolean|'range'|'simple'}
       * @default false
       */
      multiSelect: null,
      /**
       * If {@link #config-multiSelect} is configured as `true`, this is an array of dates
       * which are selected. There may be multiple, discontiguous date ranges.
       *
       * If {@link #config-multiSelect} is configured as `'range'`, this is a two element array
       * specifying the first and last selected dates in a range.
       *
       * You can also provide the selected dates as an array of `YYYY-MM-DD` date strings
       * @config {Date[]|String[]}
       */
      selection: {
        $config: {
          equal: (v1, v2) => v1 && v1.equals(v2)
        },
        value: null
      },
      /**
       * By default, the month and year are editable. Configure this as `false` to prevent that.
       * @config {Boolean}
       * @default true
       */
      editMonth: {
        $config: "lazy",
        value: null
      },
      /**
       * By default, the year is visible. Configure this as `false` to prevent that.
       * @config {Boolean}
       * @default true
       */
      includeYear: {
        $config: "lazy",
        value: null
      },
      /**
       * The {@link Core.helper.DateHelper} format string to format the day names.
       * @config {String}
       * @default
       */
      dayNameFormat: "dd",
      trapFocus: true,
      role: "grid",
      focusDescendant: true,
      /**
       * By default, when the {@link #property-date} changes, the UI will only refresh
       * if it doesn't contain a cell for that date, so as to keep a stable UI when
       * navigating.
       *
       * Configure this as `true` to refresh the UI whenever the month changes, even if
       * the UI already shows that date.
       * @config {Boolean}
       * @internal
       */
      alwaysRefreshOnMonthChange: null,
      /**
       * Configure as `true` to add a highlighted border and background to the week row which
       * encapsulates the selected date.
       * @prp {Boolean}
       * @default false
       */
      highlightSelectedWeek: null
    };
  }
  static get prototypeProperties() {
    return {
      /**
       * The class name to add to the calendar cell whose date which is outside the
       * {@link #config-minDate}/{@link #config-maxDate} range.
       * @config {String}
       * @private
       */
      outOfRangeCls: "b-out-of-range",
      /**
       * The class name to add to the currently focused calendar cell.
       * @config {String}
       * @private
       */
      activeCls: "b-active-date",
      /**
       * The class name to add to selected calendar cells.
       * @config {String}
       * @private
       */
      selectedCls: "b-selected-date"
    };
  }
  // region Init
  construct(config) {
    const me = this;
    super.construct(config);
    const { widgetMap } = me;
    me.externalCellRenderer = me.cellRenderer;
    me.cellRenderer = me.internalCellRenderer;
    me.element.setAttribute("aria-activedescendant", `${me.id}-active-day`);
    me.weeksElement.setAttribute("role", "grid");
    me.weekElements.forEach((w) => w.setAttribute("role", "row"));
    widgetMap.fields && me.element.setAttribute("ariaLabelledBy", widgetMap.fields.id);
    EventHelper.on({
      element: me.weeksElement,
      click: {
        handler: "onCellClick",
        delegate: `.${me.dayCellCls}:not(.${me.disabledCls}):not(.${me.outOfRangeCls})`
      },
      mousedown: {
        handler: "onCellMousedown",
        delegate: `.${me.dayCellCls}`
      },
      thisObj: me
    });
    me.getConfig("editMonth");
    me.getConfig("includeYear");
    me.refresh.flush();
  }
  afterHide() {
    var _a;
    (_a = this._yearPicker) == null ? void 0 : _a.hide();
    super.afterHide(...arguments);
  }
  doDestroy() {
    var _a, _b;
    (_a = this.yearButton) == null ? void 0 : _a.destroy();
    (_b = this.monthField) == null ? void 0 : _b.destroy();
    super.doDestroy();
  }
  // endregion
  get activeDayCell() {
    return this.weeksElement.querySelector(`#${this.id}-active-day`);
  }
  get focusElement() {
    return !this.disabled && this.activeDayCell || this.element;
  }
  onMonthDateChange(info) {
    const ret = super.onMonthDateChange(info);
    this.refresh.flush();
    return ret;
  }
  updateEditMonth(editMonth) {
    const { monthField, yearButton } = this.widgetMap, disallow = editMonth === false;
    monthField.readOnly = disallow;
    yearButton.disabled = disallow;
  }
  updateIncludeYear(includeYear) {
    const { yearButton } = this.widgetMap;
    yearButton.hidden = includeYear === false;
  }
  doRefresh(...args) {
    const me = this, {
      date,
      widgetMap
    } = me, oldActiveCell = me.activeDayCell, activeDate = DateHelper.betweenLesser(me.activeDate, me.month.startDate, me.month.endDate) ? me.activeDate : me._activeDate = date;
    me.getConfig("tbar");
    super.doRefresh(...args);
    widgetMap.monthField && (widgetMap.monthField.value = date.getMonth());
    widgetMap.yearButton && (widgetMap.yearButton.text = date.getFullYear());
    const dateOfOldActiveCell = DateHelper.parseKey(oldActiveCell == null ? void 0 : oldActiveCell.dataset.date);
    if (activeDate - dateOfOldActiveCell) {
      me.syncActiveDate(activeDate, dateOfOldActiveCell);
    }
  }
  internalCellRenderer({ cell, date }) {
    const me = this, {
      externalCellRenderer
    } = me, isSelected = me.isSelectedDate(date), cellClassList = me.getCellClassList(...arguments);
    if (isSelected) {
      if (me.multiSelect) {
        const isStart = !me.isSelectedDate(DateHelper.add(date, -1, "d")), isEnd = !me.isSelectedDate(DateHelper.add(date, 1, "d"));
        cellClassList["b-range-start"] = isStart;
        cellClassList["b-range-end"] = isEnd;
        cellClassList["b-in-range"] = !isStart && !isEnd;
      }
    }
    DomHelper.updateClassList(cell, cellClassList);
    cell.innerHTML = `<div class="b-datepicker-cell-inner">${date.getDate()}</div>`;
    cell.setAttribute("role", "gridcell");
    cell.setAttribute("aria-label", DateHelper.format(date, "dddd MMMM Do, YYYY"));
    DomHelper.toggleAttribute(cell, "aria-selected", isSelected);
    if (me.isActiveDate(date)) {
      cell.id = `${me.id}-active-day`;
    } else {
      cell.removeAttribute("id");
    }
    if (externalCellRenderer) {
      arguments[0].cell = cell.firstChild;
      me.callback(externalCellRenderer, this, arguments);
    }
  }
  getCellClassList({ date }) {
    const me = this, {
      activeCls,
      selectedCls
    } = me;
    return {
      [activeCls]: activeCls && me.isActiveDate(date),
      [selectedCls]: me.isSelectedDate(date),
      [me.outOfRangeCls]: me.minDate && date < me.minDate || me.maxDate && date > me.maxDate
    };
  }
  onCellMousedown(event) {
    const cell = event.target.closest("[data-date]");
    if (this.focusable) {
      cell.focus();
      if (DomHelper.getActiveElement(cell) === cell) {
        event.preventDefault();
      }
    } else {
      event.preventDefault();
    }
    this.activeDate = DateHelper.parseKey(cell.dataset.date);
  }
  onCellClick(event) {
    const cell = event.target.closest("[data-date]");
    this.onUIDateSelect(DateHelper.parseKey(cell.dataset.date), event);
  }
  /**
   * Called when the user uses the UI to select the current activeDate. So ENTER when focused
   * or clicking a date cell.
   * @param {Date} date The active date to select
   * @param {Event} event the instigating event, either a `click` event or a `keydown` event.
   * @internal
   */
  onUIDateSelect(date, event) {
    const me = this, {
      lastClickedDate,
      multiSelect
    } = me;
    if (!me.isDisabledDate(date)) {
      if (me.trigger("beforeDateSelect", { date, event }) !== false) {
        me.lastClickedDate = date;
        me.activatingEvent = event;
        if (multiSelect) {
          me.handleMultiSelect(lastClickedDate, date, event);
        } else {
          me.date = me.selection = date;
          if (me.floating) {
            me.hide();
          }
        }
        me.activatingEvent = null;
      }
    }
  }
  // Calls updateSelection if the selection is mutated
  handleMultiSelect(lastClickedDate, date, event) {
    const me = this, {
      multiSelect
    } = me, _selection = me._selection || (me._selection = new DateSet()), selection = _selection.dates, singleRange = multiSelect === "range" || multiSelect === "simple", isSimpleMultiSelect = multiSelect === "simple" || DomHelper.isTouchEvent, {
      size,
      generation
    } = _selection, rangeEnds = size && {
      [DateHelper.makeKey(DateHelper.add(selection[0], -1, "d"))]: 1,
      [DateHelper.makeKey(selection[0])]: 1,
      [DateHelper.makeKey(selection[selection.length - 1])]: 1,
      [DateHelper.makeKey(DateHelper.add(selection[selection.length - 1], 1, "d"))]: 1
    }, isSelected = _selection.has(date), toggleFn = isSelected ? "delete" : "add";
    const clickedRangeEnd = singleRange && (rangeEnds == null ? void 0 : rangeEnds[DateHelper.makeKey(date)]);
    if (isSimpleMultiSelect && size > 1) {
      _selection.clear();
      _selection.add(date);
    } else {
      if (event.ctrlKey) {
        if (multiSelect === true || !size || clickedRangeEnd) {
          _selection[toggleFn](date);
          if (singleRange && !_selection.has(me.rangeStartDate)) {
            me.rangeStartDate.setDate(me.rangeStartDate.getDate() + (date < selection[1] ? 1 : -1));
          }
        }
      } else if ((isSimpleMultiSelect || event.shiftKey) && size) {
        const [start, end] = [
          new Date(singleRange ? me.rangeStartDate || (me.rangeStartDate = selection[0]) : lastClickedDate),
          date
        ].sort(dateSort);
        if (singleRange) {
          _selection.clear();
        }
        for (const d = start; d <= end; d.setDate(d.getDate() + 1)) {
          _selection.add(d);
        }
      } else if (!(_selection.has(date) && _selection.size === 1)) {
        _selection.clear();
        _selection.add(date);
      }
    }
    const newSize = _selection.size;
    if (newSize === 1) {
      me.rangeStartDate = date;
    } else if (!newSize) {
      me.rangeStartDate = null;
    }
    if (_selection.generation !== generation) {
      me.updateSelection(_selection);
    }
  }
  updateHighlightSelectedWeek(highlightSelectedWeek) {
    this.element.classList.toggle("b-highlight-selected-week", Boolean(highlightSelectedWeek));
  }
  updateMultiSelect(multiSelect) {
    this.element.classList.toggle("b-multiselect", Boolean(multiSelect));
    if (!multiSelect) {
      this.selection = [...this.selection][0];
    }
  }
  /**
   * The selected Date(s).
   *
   * When {@link #config-multiSelect} is `'range'`, then this yields a two element array
   * representing the start and end of the selected range.
   *
   * When {@link #config-multiSelect} is `true`, this yields an array containing every selected
   * Date.
   * @member {Date[]} selection
   */
  get selection() {
    const { _selection } = this, dates = _selection ? _selection.dates : emptyArray;
    return this.multiSelect === "range" && dates.length ? [dates[0], dates[dates.length - 1]] : dates;
  }
  getRange(snapToWeeks) {
    if (this.multiSelect) {
      const { selection } = this, startDate = selection[0], endDate = selection[selection.length - 1];
      if (selection.length > 1 && DateHelper.add(endDate, 1, "d").getDate() === startDate.getDate()) {
        return {
          unit: "month",
          magnitude: DateHelper.diff(startDate, DateHelper.add(endDate, 1, "d"), "month"),
          wholeMonth: startDate.getDate() === 1 && endDate.getDate() === DateHelper.getLastDateOfMonth(endDate).getDate() && consecutiveDates(selection),
          startDate,
          endDate
        };
      }
      if (startDate.getDay() == DateHelper.weekStartDay && endDate.getDay() === (DateHelper.weekStartDay + 6) % 7) {
        return {
          unit: "week",
          magnitude: DateHelper.diff(startDate, DateHelper.add(endDate, 1, "d"), "week"),
          startDate,
          endDate
        };
      }
      if (snapToWeeks) {
        const weekSpan = Math.max(Math.ceil(DateHelper.diff(startDate, DateHelper.add(endDate, 1, "d"), "w")), 1), dayOfWeek = (startDate.getDay() - DateHelper.weekStartDay + 7) % 7, snappedStartDate = DateHelper.add(startDate, -dayOfWeek, "d"), snappedEndDate = DateHelper.add(snappedStartDate, weekSpan * 7 - 1, "d");
        return {
          unit: "week",
          magnitude: weekSpan,
          startDate: snappedStartDate,
          endDate: snappedEndDate
        };
      }
      return {
        unit: "day",
        magnitude: DateHelper.diff(startDate, endDate, "d") + 1,
        startDate,
        endDate
      };
    }
  }
  get snappedRange() {
  }
  changeSelection(selection, was) {
    const me = this;
    let result, rangeStartDate;
    if (!selection) {
      result = new DateSet();
    } else if (selection.isDateSet) {
      if (was === selection || (was == null ? void 0 : was.equals(selection))) {
        return was;
      }
      rangeStartDate = ObjectHelper.first(selection);
      result = selection;
    } else {
      if (!selection.forEach) {
        selection = [selection];
      }
      selection.forEach((d, i) => selection[i] = me.changeDate(d));
      rangeStartDate = selection[0];
      selection.sort(dateSort);
      if (me.multiSelect === "range" && selection.length === 2) {
        result = new DateSet();
        for (const d = new Date(selection[0]); d <= selection[1]; d.setDate(d.getDate() + 1)) {
          result.add(d);
        }
      } else {
        rangeStartDate = selection[0];
        result = new DateSet(selection);
      }
    }
    if (rangeStartDate) {
      me.rangeStartDate = DateHelper.clearTime(rangeStartDate);
    }
    return result;
  }
  updateSelection(dateSet, oldDateSet) {
    const me = this, { multiSelect } = me, { dates } = dateSet, selection = multiSelect === "range" ? [dates[0], dates[dates.length - 1]] : dates;
    if (!me.isConfiguring) {
      if (!multiSelect && dates.length && !me.peekConfig("date") && dates[0].getMonth() === me.month.month) {
        me.date = dates[0];
      }
      me.refresh.now();
      const event = {
        selection,
        userAction: Boolean(me.activatingEvent)
      };
      if (oldDateSet) {
        const { dates: dates2 } = oldDateSet;
        event.oldSelection = multiSelect === "range" ? [dates2[0], dates2[dates2.length - 1]] : dates2;
      }
      me.trigger("selectionChange", event);
    }
  }
  /**
   * Checks whether a date is selected.
   * @param {Date} date The date
   * @returns {Boolean}
   */
  isSelected(date) {
    return this.selection.some((d) => DateHelper.isSameDate(d, date));
  }
  onInternalKeyDown(keyEvent) {
    const me = this, keyName = keyEvent.key.trim() || keyEvent.code, activeDate = me.activeDate;
    let newDate = new Date(activeDate);
    if (keyName === "Escape" && me.floating) {
      return me.hide();
    }
    if (activeDate && me.weeksElement.contains(keyEvent.target)) {
      do {
        switch (keyName) {
          case "ArrowLeft":
            keyEvent.preventDefault();
            if (keyEvent.ctrlKey) {
              newDate = me.gotoPrevMonth();
            } else {
              newDate.setDate(newDate.getDate() - 1);
            }
            break;
          case "ArrowUp":
            keyEvent.preventDefault();
            newDate.setDate(newDate.getDate() - 7);
            break;
          case "ArrowRight":
            keyEvent.preventDefault();
            if (keyEvent.ctrlKey) {
              newDate = me.gotoNextMonth();
            } else {
              newDate.setDate(newDate.getDate() + 1);
            }
            break;
          case "ArrowDown":
            keyEvent.preventDefault();
            newDate.setDate(newDate.getDate() + 7);
            break;
          case "Enter":
            return me.onUIDateSelect(activeDate, keyEvent);
        }
      } while (me.isDisabledDate(newDate) && !me.focusDisabledDates);
      if (me.minDate && newDate < me.minDate) {
        return;
      }
      if (me.maxDate && newDate > me.maxDate) {
        return;
      }
      me.activeDate = newDate;
    }
  }
  changeMinDate(minDate) {
    return minDate && CalendarPanel.prototype.changeDate.apply(this, arguments);
  }
  updateMinDate(minDate) {
    this._yearpicker && (this._yearpicker.minYear = minDate == null ? void 0 : minDate.getFullYear());
    this.refresh();
  }
  changeMaxDate(minDate) {
    return minDate && CalendarPanel.prototype.changeDate.apply(this, arguments);
  }
  updateMaxDate(maxDate) {
    this._yearpicker && (this._yearpicker.maxYear = maxDate == null ? void 0 : maxDate.getFullYear());
    this.refresh();
  }
  changeDate(date) {
    return DateHelper.clamp(super.changeDate(date), this.minDate, this.maxDate);
  }
  updateDate(date, oldDate) {
    const me = this, { month } = me;
    if (me.isConfiguring && !me.initializingActiveDate) {
      if (!("selection" in me.initialConfig)) {
        me.selection = date;
      }
      if (!("activeDate" in me.initialConfig)) {
        me.activeDate = date;
      }
    }
    if (!month.date || date.getMonth() === month.month || !me.getCell(date) || me.alwaysRefreshOnMonthChange || me.isNavigating) {
      super.updateDate(date);
    } else {
      const newMonth = month.getOtherMonth(date), changes = me.eventListeners && (oldDate ? date.getDate() !== oldDate.getDate() | (newMonth.getWeekId(date) !== month.getWeekId(oldDate)) << 1 | (date.getMonth() !== (oldDate == null ? void 0 : oldDate.getMonth())) << 2 | (date.getFullYear() !== (oldDate == null ? void 0 : oldDate.getFullYear())) << 3 : 15);
      me.trigger("dateChange", {
        changes: {
          d: true,
          w: Boolean(changes & 2),
          m: Boolean(changes & 12),
          y: Boolean(changes & 8),
          r: newMonth.weekCount !== month.weekCount
        },
        value: date,
        oldValue: oldDate
      });
    }
  }
  changeActiveDate(activeDate, oldActiveDate) {
    const me = this;
    if (activeDate === "today") {
      activeDate = /* @__PURE__ */ new Date();
    }
    if (me.trigger("beforeActiveDateChange", { activeDate, oldActiveDate }) === false) {
      return;
    }
    activeDate = activeDate ? me.changeDate(activeDate) : me.date || (me.date = DateHelper.clearTime(/* @__PURE__ */ new Date()));
    if (isNaN(activeDate)) {
      throw new Error("DatePicker activeDate must be passed a Date, or a YYYY-MM-DD date string");
    }
    return DateHelper.clamp(activeDate, me.minDate, me.maxDate);
  }
  updateActiveDate(activeDate, wasActiveDate) {
    const me = this, { isConfiguring } = me;
    if (isConfiguring || !me.getCell(activeDate)) {
      me.initializingActiveDate = isConfiguring;
      me.date = activeDate;
      me.initializingActiveDate = false;
    }
    if (!isConfiguring && !me.refresh.isPending) {
      me.syncActiveDate(activeDate, wasActiveDate);
    }
  }
  syncActiveDate(activeDate, wasActiveDate) {
    const me = this, { activeCls } = me, activeCell = me.getCell(activeDate), wasActiveCell = wasActiveDate && me.getCell(wasActiveDate), activeElement = DomHelper.getActiveElement(me.element);
    if (activeCell) {
      me.focusable && activeCell.setAttribute("tabIndex", "0");
      activeCls && activeCell.classList.add(activeCls);
      activeCell.id = `${me.id}-active-day`;
      if (me.weeksElement.contains(activeElement)) {
        activeCell.focus();
      }
    }
    if (wasActiveCell && wasActiveCell !== activeCell) {
      me.focusable && wasActiveCell.removeAttribute("tabIndex");
      activeCls && wasActiveCell.classList.remove(activeCls);
      wasActiveCell.removeAttribute("id");
    }
  }
  set value(value) {
    const me = this, {
      selection,
      duration
    } = me;
    me.isNavigating = true;
    if (value) {
      value = me.changeDate(value, me.value);
      if (me.multiSelect === "range" && (selection == null ? void 0 : selection.length) === 2) {
        if (!DateHelper.betweenLesserEqual(value, ...selection)) {
          if (value < selection[0]) {
            me.selection = [value, DateHelper.add(value, duration - 1, "d")];
          } else {
            me.selection = [DateHelper.add(value, -(duration - 1), "d"), value];
          }
        }
        me.date = me.activeDate = value;
        return;
      }
      if (value !== void 0) {
        me.selection = value;
        me.date = me.activeDate = value;
      }
    } else {
      me.date = me.activeDate = /* @__PURE__ */ new Date();
      me.selection = null;
    }
    me.isNavigating = false;
  }
  get value() {
    const { selection } = this, n = (selection == null ? void 0 : selection.length) || null;
    return n && selection[n - 1];
  }
  get duration() {
    return this.multiSelect === "range" ? DateHelper.diff(...this.selection, "d") + 1 : 1;
  }
  gotoPrevYear() {
    return this.goto(-1, "year");
  }
  gotoPrevMonth() {
    return this.goto(-1, "month");
  }
  gotoNextMonth() {
    return this.goto(1, "month");
  }
  gotoNextYear() {
    return this.goto(1, "year");
  }
  goto(direction, unit) {
    const me = this, { activeDate } = me, activeCell = activeDate && me.getCell(activeDate);
    let newDate;
    if (unit === "month" && activeCell && (activeDate == null ? void 0 : activeDate.getMonth()) === me.month.month + direction) {
      newDate = activeDate;
    } else {
      newDate = DateHelper.add(activeCell ? activeDate : me.date, direction, unit);
    }
    const firstDateOfNewMonth = new Date(newDate);
    firstDateOfNewMonth.setDate(1);
    const lastDateOfNewMonth = DateHelper.add(DateHelper.add(firstDateOfNewMonth, 1, "month"), -1, "day");
    if (me.minDate && direction < 0 && lastDateOfNewMonth < me.minDate || me.maxDate && direction > 0 && firstDateOfNewMonth > me.maxDate) {
      return;
    }
    me.isNavigating = true;
    const result = me.date = newDate;
    if (activeCell) {
      me.activeDate = newDate;
    }
    me.isNavigating = false;
    return result;
  }
  isActiveDate(date) {
    return !(date - this.activeDate);
  }
  isSelectedDate(date) {
    var _a;
    return (_a = this._selection) == null ? void 0 : _a.has(date);
  }
  onFocusIn(ev) {
    var _a;
    super.onFocusIn(ev);
    if (ev.toWidget === this && ev._target === this.element && !this.disabled) {
      (_a = this.focusElement) == null ? void 0 : _a.focus();
    }
  }
  onMonthPicked({ record, userAction }) {
    var _a;
    if (userAction) {
      this.activeDate = DateHelper.add(this.activeDate, record.value - this.activeDate.getMonth(), "month");
      (_a = this.focusElement) == null ? void 0 : _a.focus();
    }
  }
  onYearPickerRequested() {
    const { yearPicker } = this;
    if (yearPicker.isVisible) {
      yearPicker.hide();
    } else {
      yearPicker.year = yearPicker.startYear = this.activeDate.getFullYear();
      yearPicker.show();
      yearPicker.focus();
    }
  }
  onYearPickerTitleClick() {
    this.yearPicker.hide();
  }
  onYearPicked({ value, source }) {
    var _a;
    const newDate = new Date(this.activeDate);
    newDate.setFullYear(value);
    this.activeDate = newDate;
    (_a = this.focusElement) == null ? void 0 : _a.focus({ preventScroll: true });
    source.hide();
  }
  changeYearPicker(yearPicker, oldYearPicker) {
    var _a, _b;
    return YearPicker.reconfigure(oldYearPicker, yearPicker ? YearPicker.mergeConfigs({
      owner: this,
      appendTo: this.element,
      minYear: (_a = this.minDate) == null ? void 0 : _a.getFullYear(),
      maxYear: (_b = this.maxDate) == null ? void 0 : _b.getFullYear()
    }, yearPicker) : null, this);
  }
  get childItems() {
    const { _yearPicker } = this, result = super.childItems;
    if (_yearPicker) {
      result.push(_yearPicker);
    }
    return result;
  }
  updateLocalization() {
    var _a;
    const {
      monthField
    } = this.widgetMap, newData = generateMonthNames();
    if (monthField && !this.isConfiguring && !newData.every((d, i) => d[1] === monthField.store.getAt(i).text)) {
      newData[(_a = monthField.value) != null ? _a : this.date.getMonth()].selected = true;
      monthField.items = newData;
    }
    super.updateLocalization();
  }
  changeMultiSelect(value) {
    if (value === "range" && BrowserHelper.isTouchDevice) {
      value = "simple";
    }
    return value;
  }
}
__publicField(DatePicker, "$name", "DatePicker");
__publicField(DatePicker, "type", "datepicker");
DatePicker.initClass();
DatePicker._$name = "DatePicker";
export {
  DatePicker as default
};
